import { PatientData } from './patients'

export type AlertUnion = AlertEventDataGrouped | AlertEventDataUngrouped

export type Alert = AlertEventDataUngrouped

export interface AlertTable extends Alert {
	id: string
	name: string
	alertType: string
	time: number
}

export interface AlertBulkAction extends Alert {
	email: string
}

export interface AlertExtra {
	patientID: string
	coachID: string
}

export interface AlertData {
	['start_service']?: string
	['alert_code']: string
	['alert_description']: string
	['min_tier']?: number
	['trend_duraction_days']?: number
}

export interface AlertHeaders {
	type: number
	status: string
	['patient_ID']: string
	timestamp: number
	timezone?: string
	['timezone_offset']?: 0
	['last_event_type']?: string
	['last_glucose_measure_timestamp']?: number
	['last_measure_timestamp']?: number
	['last_measure_range']?: string
	['last_glucose_measure']?: number
	['last_bp_measure_systolic']?: number
	['last_bp_measure_diastolic']?: number
	['last_bp_measure_pulse']?: number
}

export interface AlertEventData {
	data: AlertData
	extra: AlertExtra
	headers: AlertHeaders
	op: {
		addedTimestamp: string
		seenTimestamp?: string
	}
	_id: string
}

export interface AlertEventDataUngrouped extends AlertEventData {
	patientData: PatientData
}

export interface AlertEventDataGrouped {
	eventData: AlertEventData
	patientData: PatientData
}

export interface AlertFilter {
	key: string
	label: string
}

export enum ALERT_CODES_BG {
	HYPO = 'HYPO',
	HYPER = 'HYPER',
}

export enum ALERT_CODES_BP {
	HYPER_BP = 'HYPER.BP',
	STAGE2_BP = 'STAGE2.BP',
}

export enum ALERT_CODES_MSK {
	MSK_RPE_DOWN = 'msk_rpe_down',
	MSK_RPE_UP = 'msk_rpe_up',
	MSK_PAIN_LEVEL = 'msk_pain_level',
	MSK_FEEDBACK_QUESTION = 'msk_feedback_question',
}

export enum ALERT_CODES_SINGLE {
	NEW_PATIENT = 'NewPatient',
	INACTIVE = 'INACTIVE',
}

export enum ALERT_CODES_TRANSCRIPTIONS {
	HYPO = 'Hypoglycemia',
	HYPER = 'Hyperglycemia',

	HYPER_BP = 'Hypertensive crisis',
	STAGE2_BP = 'Stage 2',

	MSK_RPE_DOWN = 'Decrease in RPE',
	MSK_RPE_UP = 'Increase in RPE',
	MSK_PAIN_LEVEL = 'Increase in a pain level',
	MSK_FEEDBACK_QUESTION = 'Screening - Pain level high (Q5)',

	NEW_PATIENT = 'New Patient',
	INACTIVE = 'Inactive Patient',
}

/**
 * Alerts state
 */

export interface AlertsState {
	filters: Array<AlertFilter>
	list: Array<Alert>
	selected: Array<string>
	searchValue: string | number
}

export enum ALERTS_ACTION_TYPES {
	GET = 'ALERTS_GET',
	SELECT = 'ALERTS_SELECT_ONE',
	DESELECT = 'ALERTS_DESELECT_ONE',
	SELECT_ALL = 'ALERTS_SELECT_ALL',
	DESELECT_ALL = 'ALERTS_DESELECT_ALL',
	APPLY_FILTERS = 'ALERTS_APPLY_FILTERS',
	REMOVE_FILTER = 'ALERTS_REMOVE_FILTER',
	REMOVE_ALL_FILTERS = 'ALERTS_REMOVE_ALL_FILTERS',
	SET_SEARCH_VALUE = 'ALERTS_SET_SEARCH_VALUE',
	RESET_ALERTS = 'ALERTS_RESET',
	REMOVE_MANAGED = 'ALERTS_REMOVE_MANAGED',
}

export interface AlertsGet {
	type: ALERTS_ACTION_TYPES.GET
	payload: Array<Alert>
}

export interface SelectAlert {
	type: ALERTS_ACTION_TYPES.SELECT
	payload: string
}

export interface DeselectAlert {
	type: ALERTS_ACTION_TYPES.DESELECT
	payload: string
}

export interface SelectAllAlerts {
	type: ALERTS_ACTION_TYPES.SELECT_ALL
}

export interface DeselectAllAlerts {
	type: ALERTS_ACTION_TYPES.DESELECT_ALL
}

export interface ApplyAlertFilters {
	type: ALERTS_ACTION_TYPES.APPLY_FILTERS
	payload: Array<AlertFilter>
}

export interface RemoveAlertFilter {
	type: ALERTS_ACTION_TYPES.REMOVE_FILTER
	payload: string | number
}

export interface RemoveAllAlertFilters {
	type: ALERTS_ACTION_TYPES.REMOVE_ALL_FILTERS
}

export interface SetSearchValueForAlerts {
	type: ALERTS_ACTION_TYPES.SET_SEARCH_VALUE
	payload: string | number
}

export interface ResetAlertsAction {
	type: ALERTS_ACTION_TYPES.RESET_ALERTS
}

export interface RemoveManagedAlertsAction {
	type: ALERTS_ACTION_TYPES.REMOVE_MANAGED
	payload: string[]
}

export type AlertsAction =
	| AlertsGet
	| SelectAlert
	| DeselectAlert
	| SelectAllAlerts
	| DeselectAllAlerts
	| ApplyAlertFilters
	| RemoveAlertFilter
	| RemoveAllAlertFilters
	| SetSearchValueForAlerts
	| ResetAlertsAction
	| RemoveManagedAlertsAction
